import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material.module';
import { SharedModule } from './shared/shared.module';
import { JwtModule } from '@auth0/angular-jwt';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideNgxWebstorage, withLocalStorage } from 'ngx-webstorage';

import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { EffectsModule } from '@ngrx/effects';
import { ROOT_REDUCERS, metaReducers } from './state/root.reducer';

import { UserEffects } from './state/user.effect';
import { RootEffects } from './state/root.effects';
import { MessagesEffects } from './state/messages.effects';
import { ConferencingEffects } from './state/conferencing.effects';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { StbEffects } from './state/stb.effects';
import { PresenceEffects } from './state/presence.effects';

Sentry.init({
  dsn: 'https://a9c0d648168d4d36b865cd9b698733d8@o409495.ingest.sentry.io/5450481',
  environment: environment.env,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // tracePropagationTargets moved to top level in Sentry v8
  tracePropagationTargets: [
    'localhost',
    'https://tv.caremessenger.co.uk',
    'https://tv-staging.caremessenger.co.uk',
    'https://tv-dev.caremessenger.co.uk',
    'https://tv-demo.caremessenger.co.uk',
    'https://stb-dev.caremessenger.co.uk',
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: environment.env === 'prod' ? 0.3 : 1.0,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: environment.env === 'prod' ? 0.3 : 1.0,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
});

export function tokenGetter(): string {
  return localStorage.getItem('access_token');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    MaterialModule,
    AppRoutingModule,
    CoreModule,
    SharedModule,
    BrowserAnimationsModule,
    JwtModule.forRoot({
      config: {
        tokenGetter,
        allowedDomains: ['localhost', 'caremessenger.co.uk', '172.17.10.222'],
      },
    }),
    StoreModule.forRoot(ROOT_REDUCERS, { metaReducers }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    connectInZone: true}),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([
      UserEffects,
      RootEffects,
      ConferencingEffects,
      MessagesEffects,
      StbEffects,
      PresenceEffects,
    ]),
  ],
  providers: [
    provideHttpClient(withInterceptors([tokenInterceptor])),
    provideTranslateService(),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),
    provideNgxWebstorage(withLocalStorage()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
